// components/trade-item-dialog.js 
import React, { useState, useEffect } from 'react';
import { useRecordContext } from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Grid,
  CircularProgress,
  Chip,
  Box,
  Tabs,
  Tab,
  Divider,
  Avatar,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  minHeight: 48,
}));

const LabelValue = ({ label, value, xs = 4 }) => (
  <>
    <Grid item xs={xs}>
      <Typography variant="body2" fontWeight="bold" color="textSecondary">
        {label}
      </Typography>
    </Grid>
    <Grid item xs={12 - xs}>
      <Typography variant="body2">{value ?? 'N/A'}</Typography>
    </Grid>
  </>
);

const formatDate = (dateString) =>
  dateString ? new Date(dateString).toLocaleString() : 'N/A';

const TradeItemDialog = ({ source = 'itemId' }) => {
  const record = useRecordContext();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [itemData, setItemData] = useState(null);
  const [error, setError] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const token = JSON.parse(localStorage.getItem("token"));

  const { type, tradeType, itemId } = record || {};

  // Determine API endpoint based on type
  const getApiEndpoint = () => {
    const baseUrl = process.env.REACT_APP_URL;
    switch (type) {
      case 'Crop':
        return `${baseUrl}/admin/offers/${itemId}`;
      case 'Input':
        return `${baseUrl}/admin/inputs/${itemId}`;
      case 'Service':
        return `${baseUrl}/admin/services/${itemId}`;
      default:
        return null;
    }
  };

  // Determine display text based on tradeType
  const getDisplayText = () => {
    switch (tradeType) {
      case 'Offer':
        return `${type} Offer`;
      case 'Demand':
        return `${type} Demand`;
      default:
        return `${type || 'Item'}`;
    }
  };

  const fetchItemDetails = async () => {
    if (!itemId || !type) return;

    setLoading(true);
    setError(null);

    const endpoint = getApiEndpoint();
    if (!endpoint) {
      setError('Invalid item type');
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(endpoint, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch ${type.toLowerCase()}: ${response.status}`);
      }

      const data = await response.json();
      setItemData(data);
    } catch (err) {
      console.error(`Failed to fetch ${type.toLowerCase()} details:`, err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = () => {
    setOpen(true);
    if (!itemData && itemId) {
      fetchItemDetails();
    }
  };

  const handleClose = () => {
    setOpen(false);
    setTabValue(0);
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  // Common fields component that can be reused
  const CommonFields = ({ data }) => (
    <>
      <LabelValue label="ID" value={data.id} />
      <LabelValue label="Status" value={
        <Chip
          label={data.status || 'Unknown'}
          size="small"
          color={
            data.status === 'Active' || data.status === 'active' ? 'success' :
              data.status === 'Completed' || data.status === 'sold' ? 'info' :
                data.status === 'Cancelled' || data.status === 'inactive' ? 'error' : 'default'
          }
        />
      } />
      <LabelValue label="Created At" value={formatDate(data.created_at)} />
      <LabelValue label="Is In Kind" value={data.is_in_kind ? 'Yes' : 'No'} />
      {data.location && (
        <LabelValue label="Location" value={data.location} />
      )}
    </>
  );

  const renderOfferInputDetails = (data, itemType) => {
    const isOffer = type === 'Crop';

    return (
      <Box>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <StyledTab label="Basic Info" />
          <StyledTab label="Location" />
          <StyledTab label="User Info" />
        </Tabs>

        {tabValue === 0 && (
          <Grid container spacing={2}>
            <LabelValue label="Title/Name" value={isOffer ? data.offer : data.name} />
            <LabelValue label="Description" value={isOffer ? data.description : data.description} />
            <LabelValue label="Price" value={`${data.price || 'N/A'} ${data.currency || 'SDG'}`} />
            <LabelValue label="Quantity" value={data.quantity} />

            {/* Category Information */}
            {data.category && (
              <LabelValue label="Category" value={data.category.name} />
            )}
            {data.subCategory && (
              <LabelValue label="Sub Category" value={data.subCategory.name} />
            )}

            {/* Input-specific fields */}
            {!isOffer && data.input_category && (
              <LabelValue label="Category" value={data.input_category.name} />
            )}
            {!isOffer && data.ingredients && (
              <LabelValue label="Ingredients" value={data.ingredients} />
            )}
            {!isOffer && data.application && (
              <LabelValue label="Application" value={data.application} />
            )}
            {!isOffer && data.dose && (
              <LabelValue label="Dose" value={data.dose} />
            )}

            <CommonFields data={data} />
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid container spacing={2}>
            {data._state && (
              <LabelValue label="State" value={data._state.name} />
            )}
            {data._city && (
              <LabelValue label="City" value={data._city.name} />
            )}
            {data._locality && (
              <LabelValue label="Locality" value={data._locality?.name} />
            )}
            {data._market && (
              <LabelValue label="Market" value={data._market?.name} />
            )}
            {data._unit && (
              <LabelValue label="Unit" value={data._unit.name} />
            )}
            {data.location && (
              <LabelValue xs={3} label="Detailed Location" value={data.location} />
            )}
          </Grid>
        )}

        {tabValue === 2 && data.user && (
          <Grid container spacing={2}>
            <LabelValue label="Owner Name" value={data.user.name} />
            <LabelValue label="Phone Number" value={data.user.phonenumber} />
            <LabelValue label="User Type" value={data.user.type} />
            <LabelValue label="City" value={data.user.city?.name} />
          </Grid>
        )}

        {/* Images section */}
        {data.images && data.images.length > 0 && tabValue === 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Images ({data.images.length})
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {data.images.map((img, index) => (
                <Avatar
                  key={index}
                  variant="rounded"
                  src={img}
                  sx={{ width: 80, height: 80, cursor: 'pointer' }}
                  onClick={() => window.open(img, '_blank')}
                />
              ))}
            </Box>
          </>
        )}
      </Box>
    );
  };

  const renderServiceDetails = (data) => {
    return (
      <Box>
        <Tabs value={tabValue} onChange={handleTabChange} sx={{ mb: 2 }}>
          <StyledTab label="Service Details" />
          <StyledTab label="Location" />
          <StyledTab label="User Info" />
        </Tabs>

        {tabValue === 0 && (
          <Grid container spacing={2}>
            <LabelValue label="Service Type" value={data.service?.name} />
            <LabelValue label="Description" value={data.description} />
            <LabelValue label="Service Type" value={data.service_type} />
            {data.price_per_day && (
              <LabelValue label="Price Per Day" value={data.price_per_day} />
            )}
            {data.quantity && (
              <LabelValue label="Quantity" value={data.quantity} />
            )}
            {data.number_of_workers && (
              <LabelValue label="Number of Workers" value={data.number_of_workers} />
            )}
            {data.vehicleType && (
              <LabelValue label="Vehicle Type" value={data.vehicleType.name} />
            )}

            {/* Service-specific fields */}
            {data.payload && (
              <LabelValue label="Payload" value={data.payload} />
            )}
            {data.line && (
              <LabelValue label="Line" value={data.line} />
            )}
            {data.temperature && (
              <LabelValue label="Temperature" value={data.temperature} />
            )}
            {data.height && (
              <LabelValue label="Height" value={data.height} />
            )}
            {data.space && (
              <LabelValue label="Space" value={data.space} />
            )}
            {data.donation_capacity && (
              <LabelValue label="Donation Capacity" value={data.donation_capacity} />
            )}

            <CommonFields data={data} />
          </Grid>
        )}

        {tabValue === 1 && (
          <Grid container spacing={2}>
            {data._state && (
              <LabelValue label="State" value={data._state.name} />
            )}
            {data._city && (
              <LabelValue label="City" value={data._city.name} />
            )}
            {data._locality && (
              <LabelValue label="Locality" value={data._locality?.name} />
            )}
            {data.location && (
              <LabelValue xs={3} label="Detailed Location" value={data.location} />
            )}
          </Grid>
        )}

        {tabValue === 2 && data.user && (
          <Grid container spacing={2}>
            <LabelValue label="Owner Name" value={data.user.name} />
            <LabelValue label="Phone Number" value={data.user.phonenumber} />
            <LabelValue label="User Type" value={data.user.type} />
          </Grid>
        )}
      </Box>
    );
  };

  // Render content based on type
  const renderContent = () => {
    if (loading) {
      return (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return (
        <Typography color="error" align="center" p={3}>
          Error loading details: {error}
        </Typography>
      );
    }

    if (!itemData) {
      return (
        <Typography align="center" p={3}>
          No data available
        </Typography>
      );
    }

    // Render based on type
    switch (type) {
      case 'Crop':
      case 'Input':
        return renderOfferInputDetails(itemData, type);
      case 'Service':
        return renderServiceDetails(itemData);
      default:
        return <Typography>Unknown item type</Typography>;
    }
  };

  if (!itemId || !type) {
    return (
      <Typography variant="body2" color="textSecondary">
        No item associated
      </Typography>
    );
  }

  return (
    <>
      <Typography
        onClick={handleOpen}
        variant="body2"
        color="primary"
        sx={{
          textDecoration: 'underline',
          cursor: 'pointer',
          display: 'inline'
        }}
      >
        {getDisplayText()} #{itemId}
      </Typography>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
        <DialogTitle>
          {getDisplayText()} Details
          <Chip
            label={tradeType || 'Unknown'}
            color={tradeType === 'Offer' ? 'primary' : 'secondary'}
            size="small"
            sx={{ ml: 2 }}
          />
        </DialogTitle>
        <DialogContent dividers>
          {renderContent()}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default TradeItemDialog;